import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPrint, FaFileExcel, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getVendorAging, clearVendorAging, clearError, clearSuccess } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';

const VendorAging = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { vendorAging, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [date, setDate] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasFiltered, setHasFiltered] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const SORT_FIELDS = { VENDOR: 'name', BALANCE: 'balance' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  // Load data on mount
  useEffect(() => {
    setTitle('accountingReports.vendorAgingTitle', { defaultValue: 'تقرير أعمار ديون الموردين' });
    // Load data automatically on mount (without toast)
    dispatch(getVendorAging({}));
    return () => {
      dispatch(clearVendorAging());
    };
  }, [dispatch, setTitle]);

  // Only show toast if user has filtered
  useEffect(() => {
    if (success && hasFiltered) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
      setHasFiltered(false); // Reset after showing toast
    } else if (success) {
      // Clear success without showing toast if it's initial load
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, hasFiltered]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = {};
    if (date) params.date = date;
    dispatch(getVendorAging(params));
  };

  const preparePdfPages = () => {
    const topHeader = document.getElementById('vendor-aging-pdf-measure-top-header');
    const thead = document.getElementById('vendor-aging-pdf-measure-thead');
    const footer = document.getElementById('vendor-aging-pdf-measure-footer');
    const tbody = document.getElementById('vendor-aging-pdf-measure-tbody');
    const chunks = calculatePdfPagination(sortedRows, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
    if (chunks.length === 0) {
      return [{ pageNumber: 1, rows: [], startIndex: 0, isFirstPage: true, isLastPage: true }];
    }
    return chunks.map((chunk, idx) => ({
      pageNumber: idx + 1,
      rows: chunk.items || chunk,
      startIndex: chunk.startIndex ?? idx * 10,
      isFirstPage: idx === 0,
      isLastPage: idx === chunks.length - 1,
    }));
  };

  const handlePreviewAll = () => {
    if (!vendorAging) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!vendorAging) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      const pages = preparePdfPages();
      setPdfPages(pages);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`vendor-aging-pdf-all-${i}`)
          ? `vendor-aging-pdf-all-${i}`
          : `preview-vendor-aging-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_VendorAging_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!vendorAging) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      const detailRows = sortedRows.map((row) => {
        const totalBalance = row.balance || 0;
        const detail = {
          [t('accountingReports.vendor', { defaultValue: 'المورد' })]: isRTL ? (row.name_ar || row.name_en || '') : (row.name_en || row.name_ar || ''),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totalBalance
        };
        bucketLabels.forEach((bucket) => {
          detail[bucket.label] = row.aging?.[bucket.key] || 0;
        });
        return detail;
      });

      if (detailRows.length === 0) {
        detailRows.push({
          [t('accountingReports.vendor', { defaultValue: 'المورد' })]: t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: 0,
          ...bucketLabels.reduce((acc, bucket) => ({ ...acc, [bucket.label]: 0 }), {})
        });
      }

      const totalsRow = {
        [t('accountingReports.vendor', { defaultValue: 'المورد' })]: t('accountingReports.total', { defaultValue: 'الإجمالي' }),
        [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: totals.total_balance || 0
      };
      bucketLabels.forEach((bucket) => {
        totalsRow[bucket.label] = totals[bucket.key] || 0;
      });
      detailRows.push(totalsRow);

      const detailsSheet = XLSX.utils.json_to_sheet(detailRows);
      XLSX.utils.book_append_sheet(workbook, detailsSheet, t('accountingReports.vendorAging', { defaultValue: 'أعمار الموردين' }));

      const summarySheet = XLSX.utils.json_to_sheet([
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.totalBalance', { defaultValue: 'إجمالي الرصيد' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals.total_balance || 0
        },
        ...bucketLabels.map((bucket) => ({
          [t('accountingReports.item', { defaultValue: 'البند' })]: bucket.label,
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: totals[bucket.key] || 0
        })),
        {
          [t('accountingReports.item', { defaultValue: 'البند' })]: t('accountingReports.date', { defaultValue: 'التاريخ' }),
          [t('accountingReports.amount', { defaultValue: 'المبلغ' })]: formatDate(meta?.date || date)
        }
      ]);
      XLSX.utils.book_append_sheet(workbook, summarySheet, t('accountingReports.summary', { defaultValue: 'ملخص' }));

      XLSX.writeFile(workbook, `VendorAging_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  // Handle both response structures: {success, message, data: {...}} or direct data object
  const reportData = vendorAging?.data?.data ? vendorAging.data : (vendorAging?.data || vendorAging || {});
  const agingRows = Array.isArray(reportData?.data) ? reportData.data : [];
  const totals = reportData?.totals || {};
  const meta = reportData?.meta || {};

  const bucketLabels = [
    { key: '0-30', label: '0-30' },
    { key: '31-60', label: '31-60' },
    { key: '61-90', label: '61-90' },
    { key: '90+', label: '90+' }
  ];

  const sortedRows = useMemo(() => {
    if (!sortField || !agingRows.length) return agingRows;
    return [...agingRows].sort((a, b) => {
      if (sortField === 'name') {
        const aName = (a.name_ar || a.name_en || '').toLowerCase();
        const bName = (b.name_ar || b.name_en || '').toLowerCase();
        return sortOrder === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      const aVal = Number(a[sortField] ?? 0);
      const bVal = Number(b[sortField] ?? 0);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [agingRows, sortField, sortOrder]);

  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h4 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.vendorAgingTitle', { defaultValue: 'تقرير أعمار ديون الموردين' })}
        </h4>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{ 
          backgroundColor: '#1a1f2e', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.date', { defaultValue: 'التاريخ' })}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#666' : '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
                opacity: isLoading ? 0.6 : 1,
                minWidth: '160px'
            }}
          >
            {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
          </button>
          </div>
        </form>

        {/* Action Buttons */}
        {vendorAging && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {vendorAging && (
          <div style={{ 
            backgroundColor: '#1a1f2e', 
            padding: '20px', 
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '15px', direction: dir, textAlign }}>
              <div><strong>{t('accountingReports.date', { defaultValue: 'التاريخ' })}:</strong> {formatDate(meta?.date || date)}</div>
            </div>
            <div style={{ overflowX: 'auto', backgroundColor: '#111826', borderRadius: '8px', padding: '12px', direction: dir }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', color: '#eee', fontSize: '13px', direction: dir }}>
                <thead>
                  <tr style={{ backgroundColor: '#252f3f' }}>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', width: '36px' }}>#</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>{t('accountingReports.vendor', { defaultValue: 'المورد' })}{getSortIcon('name')}</th>
                    <th style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('balance')}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}{getSortIcon('balance')}</th>
                    {bucketLabels.map((bucket) => (
                      <th key={bucket.key} style={{ borderBottom: '1px solid #333', padding: '10px', textAlign: 'center' }}>{bucket.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={3 + bucketLabels.length} style={{ padding: '12px', textAlign: 'center', color: '#bbb' }}>
                        {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                      </td>
                    </tr>
                  )}
                  {sortedRows.map((row, idx) => {
                    const name = isRTL ? (row.name_ar || row.name_en || '') : (row.name_en || row.name_ar || '');
                    const totalBalance = row.balance || 0;
                    return (
                      <tr key={row.vendor_id || row.id || idx} style={{ borderBottom: '1px solid #222' }}>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{name}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>{formatNumber(totalBalance)}</td>
                        {bucketLabels.map((bucket) => (
                          <td key={bucket.key} style={{ padding: '10px', textAlign: 'center' }}>
                            {formatNumber(row.aging?.[bucket.key] || 0)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#1f2937', fontWeight: 'bold' }}>
                    <td style={{ padding: '10px', textAlign: 'center' }} colSpan={2}>
                      {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{formatNumber(totals.total_balance || 0)}</td>
                    {bucketLabels.map((bucket) => (
                      <td key={bucket.key} style={{ padding: '10px', textAlign: 'center' }}>
                        {formatNumber(totals[bucket.key] || 0)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* PDF Print Templates (one full page per chunk) */}
      {pdfType === 'all' && vendorAging && pdfPages && pdfPages.length > 0 && (
        pdfPages.map((page, pageIndex) => (
          <VendorAgingPdfPage
            key={pageIndex}
            idPrefix="vendor-aging-pdf-all"
            page={page}
            pageIndex={pageIndex}
            isRTL={isRTL}
            dir={dir}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            date={date}
            meta={meta}
            totals={totals}
            bucketLabels={bucketLabels}
            formatNumber={formatNumber}
            formatDate={formatDate}
            hidden
          />
        ))
      )}

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!vendorAging}
        title={t('accountingReports.vendorAgingTitle', { defaultValue: 'تقرير أعمار ديون الموردين' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
        contentWidth="794px"
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          {pdfPages && pdfPages.length > 0 && pdfPages.map((page, pageIndex) => (
            <VendorAgingPdfPage
              key={pageIndex}
              idPrefix="preview-vendor-aging-pdf-all"
              page={page}
              pageIndex={pageIndex}
              isRTL={isRTL}
              dir={dir}
              t={t}
              publicUrl={publicUrl}
              headerLogo={headerLogo}
              date={date}
              meta={meta}
              totals={totals}
              bucketLabels={bucketLabels}
              formatNumber={formatNumber}
              formatDate={formatDate}
            />
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden measurement template used to calculate dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="vendor-aging-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <h2 style={{ textAlign: 'center', fontSize: '20px', margin: '12px 0' }}>{t('accountingReports.vendorAgingTitle', { defaultValue: 'تقرير أعمار ديون الموردين' })}</h2>
          <div style={{ textAlign: 'center', fontSize: '14px', marginBottom: '20px' }}>{formatDate(meta?.date || date)}</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #000' }}>
          <thead id="vendor-aging-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.vendor', { defaultValue: 'المورد' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
              {bucketLabels.map((bucket) => (
                <th key={bucket.key} style={{ border: '1px solid #000', padding: '6px' }}>{bucket.label}</th>
              ))}
            </tr>
          </thead>
          <tbody id="vendor-aging-pdf-measure-tbody">
            {agingRows.slice(0, 5).map((row, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{row.name_ar || row.name_en || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{row.balance || '—'}</td>
                {bucketLabels.map((bucket) => (
                  <td key={bucket.key} style={{ border: '1px solid #000', padding: '6px' }}>{row.aging?.[bucket.key] || '—'}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div id="vendor-aging-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

const VendorAgingPdfPage = ({
  idPrefix,
  page,
  pageIndex,
  isRTL,
  dir,
  t,
  publicUrl,
  headerLogo,
  date,
  meta,
  totals,
  bucketLabels,
  formatNumber,
  formatDate,
  hidden = false,
}) => {
  const rows = page.rows || [];
  return (
    <div
      id={`${idPrefix}-${pageIndex}`}
      aria-hidden={hidden || undefined}
      style={{
        position: hidden ? 'absolute' : 'static',
        left: hidden ? '-9999px' : undefined,
        top: hidden ? 0 : undefined,
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible',
        boxShadow: hidden ? undefined : '0 4px 20px rgba(0, 0, 0, 0.5)',
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '600px',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.1,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                  {t('accountingReports.vendorAgingTitle', { defaultValue: 'تقرير أعمار ديون الموردين' })}
                </h2>
                <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                  {formatDate(meta?.date || date)}
                </div>
              </div>
              <div style={{ color: '#000', fontSize: '13px', marginTop: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', direction: isRTL ? 'rtl' : 'ltr' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center', width: '30px' }}>#</th>
                      <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                        {t('accountingReports.vendor', { defaultValue: 'المورد' })}
                      </th>
                      <th style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                        {t('accountingReports.balance', { defaultValue: 'الرصيد' })}
                      </th>
                      {bucketLabels.map((bucket) => (
                        <th key={bucket.key} style={{ border: '1px solid #000', padding: '6px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>
                          {bucket.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={3 + bucketLabels.length} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                          {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                        </td>
                      </tr>
                    )}
                    {rows.map((row, idx) => {
                      const name = isRTL ? (row.name_ar || row.name_en || '') : (row.name_en || row.name_ar || '');
                      const totalBalance = row.balance || 0;
                      return (
                        <tr key={row.vendor_id || row.id || idx}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{page.startIndex + idx + 1}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{name}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: 'bold' }}>{formatNumber(totalBalance)}</td>
                          {bucketLabels.map((bucket) => (
                            <td key={bucket.key} style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>
                              {formatNumber(row.aging?.[bucket.key] || 0)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {page.isLastPage && (
                      <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }} colSpan={2}>
                          {t('accountingReports.total', { defaultValue: 'الإجمالي' })}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{formatNumber(totals.total_balance || 0)}</td>
                        {bucketLabels.map((bucket) => (
                          <td key={bucket.key} style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                            {formatNumber(totals[bucket.key] || 0)}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '18px',
            right: '18px',
            flexShrink: 0
          }}>
            <img
              src={`${publicUrl}/FooterAce.png`}
              alt="Footer"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderTop: '4px double #000',
                paddingTop: '8px',
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAging;
